/**
 * useUploadHandler - Manages upload success callbacks with dedup logic.
 */

import { useCallback } from 'react';
import { toast } from 'sonner';
import type { ProductAsset } from '@/types/products';
import type { PendingUpload, PendingUploadResultForCallback } from './types';

// Global set to prevent duplicate uploads across React StrictMode double-renders
const globalPendingUploadKeys = new Set<string>();

function addPendingUploadKey(key: string): boolean {
  if (globalPendingUploadKeys.has(key)) return false;
  globalPendingUploadKeys.add(key);
  setTimeout(() => globalPendingUploadKeys.delete(key), 2000);
  return true;
}

export function useUploadHandler(
  groupedByChannel: Record<string, ProductAsset[]>,
  setPendingUploads: React.Dispatch<React.SetStateAction<PendingUpload[]>>,
  setChangedChannelKeys: (fn: (prev: Set<string>) => Set<string>) => void,
) {
  return useCallback(
    (channelId?: string, channelKey?: string, uploadResult?: PendingUploadResultForCallback) => {
      if (!channelId || !channelKey || !uploadResult) return;

      const allAssets = Object.values(groupedByChannel).flat();
      const alreadyLinked = allAssets.some(
        (a) => a.asset_id === uploadResult.assetId && a.channel_id === channelId
      );
      if (alreadyLinked) {
        toast.info('Dieses Bild ist bereits in diesem Kanal vorhanden');
        return;
      }

      const uploadKey = `${uploadResult.assetId}-${channelId}`;
      if (!addPendingUploadKey(uploadKey)) return;

      setPendingUploads((prev) => {
        const isDuplicate = prev.some(
          (p) => p.assetId === uploadResult.assetId && p.channelId === channelId
        );
        if (isDuplicate) return prev;
        return [...prev, {
          assetId: uploadResult.assetId,
          channelId,
          channelKey,
          width: uploadResult.width,
          height: uploadResult.height,
        }];
      });
      setChangedChannelKeys((prev) => new Set(prev).add(channelKey));
    },
    [groupedByChannel, setPendingUploads, setChangedChannelKeys]
  );
}
